import { useCallback } from "react";

import { secondsToMilliseconds } from "~/tools/date-time";
import { identity } from "~/tools/identity";
import { isPresent } from "~/tools/is-present";
import { toEntries } from "~/tools/to-entries";

import { getMainMenuFn } from "../common/openapi/api";
import type { MainMenuGroup, MainMenuViewItem } from "../common/openapi/models";

import type { UseQueryOptions, UseQueryResult } from "./react-query";
import { useQuery } from "./react-query";

const KUBERNETES_MENU_GROUP_IDENTIFIER = "urn:stackpack:kubernetes-v2:shared:main-menu-group:kubernetes";

const KUBERNETES_MAIN_MENU_ITEM_GROUPS = {
	"k8s-services": ["services"],
	"k8s-workloads": ["pods", "deployments", "statefulsets", "daemonsets", "jobs", "cronjobs"],
	"k8s-storage": ["persistent-volume-claims", "persistent-volumes"],
	"k8s-configuration": ["configmaps", "secrets"],
	"k8s-infrastructure": ["clusters", "namespaces", "nodes"],
};

const getMainMenuItemGroup = (viewIdentifier: string): undefined | MainMenuKubernetesViewItemGroup => {
	const group = toEntries(KUBERNETES_MAIN_MENU_ITEM_GROUPS).find(([, postfixes]) =>
		postfixes.some((postfix) => viewIdentifier.endsWith(`:${postfix}`)),
	);
	return group?.at(0) as MainMenuKubernetesViewItemGroup;
};

export type MainMenuKubernetesViewItemGroup = keyof typeof KUBERNETES_MAIN_MENU_ITEM_GROUPS;

export interface MainMenuKubernetesViewItem extends MainMenuViewItem {
	group: MainMenuKubernetesViewItemGroup;
}

export type MainMenuGroupExtended =
	| (MainMenuGroup & { isKubernetes?: false })
	| (Omit<MainMenuGroup, "items"> & {
			isKubernetes: true;
			items: Array<MainMenuKubernetesViewItem>;
	  });

const extendMainMenuGroup = (menuGroup: MainMenuGroup): MainMenuGroupExtended => {
	if (menuGroup.identifier !== KUBERNETES_MENU_GROUP_IDENTIFIER) {
		return { ...menuGroup, isKubernetes: false };
	}
	return {
		...menuGroup,
		isKubernetes: true,
		items: menuGroup.items
			.map((item) => {
				const group = getMainMenuItemGroup(item.identifier);
				return group == null ? null : { ...item, group };
			})
			.filter(isPresent),
	};
};

export const useActiveMainMenuExtensionGroup = (
	activeViewIdentifier: undefined | string,
): MainMenuGroupExtended | undefined => {
	const { data } = useQueryMainMenuGroups({
		select: useCallback(
			(groups: Array<MainMenuGroupExtended>) => {
				if (activeViewIdentifier == null) {
					return undefined;
				}
				return groups.find(({ items }) => items.find(({ identifier }) => identifier === activeViewIdentifier));
			},
			[activeViewIdentifier],
		),
	});
	return data;
};

export const useActiveMainMenuViewItem = (activeViewIdentifier: undefined | string): MainMenuViewItem | undefined => {
	const { data } = useQueryMainMenuGroups({
		select: useCallback(
			(groups: Array<MainMenuGroupExtended>) => {
				if (activeViewIdentifier == null) {
					return undefined;
				}
				return groups.flatMap(({ items }) => items).find(({ identifier }) => identifier === activeViewIdentifier);
			},
			[activeViewIdentifier],
		),
	});
	return data;
};

export const useQueryMainMenuGroups = <TResult = Array<MainMenuGroupExtended>>({
	select = identity as (groups: Array<MainMenuGroupExtended>) => TResult,
	...options
}: UseQueryOptions<Array<MainMenuGroupExtended>, Error, TResult> = {}): UseQueryResult<TResult, Error> =>
	useQuery<Array<MainMenuGroup>, Error, TResult>({
		queryKey: ["mainMenuGroups"],
		queryFn: getMainMenuFn,
		...mainMenuGroupQueryOptions,
		...options,
		select: useCallback((groups: Array<MainMenuGroup>) => select(groups.map(extendMainMenuGroup)), [select]),
	});

const mainMenuGroupQueryOptions = {
	// Allowing for stale data to avoid hitting the api multiple times in short time. This data doesn't change frequently.
	staleTime: secondsToMilliseconds(30),
	cacheTime: Number.POSITIVE_INFINITY,
};
